import { DefaultError, useMutation } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { IMusicProductDigitalImageResponse } from './types/query';

export const usePostMusicProductDigitalImageThumbnail = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostMusicProductDigitalImageThumbnail'
  );
  return useMutation<
    IMusicProductDigitalImageResponse,
    DefaultError,
    { file: File; id: string }
  >({
    mutationFn: async ({ file, id }) => {
      loading(
        i18next.t('service-message.add.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );
      const formData = new FormData();

      formData.append('file', file);

      const response = await apiClient<FormData>({
        method: 'POST',
        url: `MusicProductDigital/Thumbnail`,
        body: formData,
        params: { mpdid: id },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePostMusicProductDigitalImageThumbnail',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.add.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutMusicProductDigitalImageThumbnail = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutMusicProductDigitalImageThumbnail'
  );
  return useMutation<{}, DefaultError, File>({
    mutationFn: async file => {
      loading(
        i18next.t('service-message.change.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );

      const formData = new FormData();

      formData.append('file', file);

      const response = await apiClient<FormData>({
        method: 'PUT',
        url: `MusicProductDigital/Thumbnail`,
        body: formData,
        params: { mpdid: id },
        withContentType: false,
      });

      return responseHandler(response, {
        groupName: 'usePutMusicProductDigitalImageThumbnail',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.change.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteMusicProductDigitalImageThumbnail = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteMusicProductDigitalImageThumbnail'
  );
  return useMutation<{}, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );

      const response = await apiClient<FormData>({
        method: 'DELETE',
        url: `MusicProductDigital/${id}/Thumbnail`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteMusicProductDigitalImageThumbnail',
        notFoundPath: '/music-product-digital',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('music-product-digital.album-cover-what'),
        })
      );
    },
    onError: dismiss,
  });
};
